import React, { useEffect, useMemo, useState } from 'react';
import ConstructionLayout from './ConstructionLayout';
import TablePagination from './TablePagination';
import { useModule } from '../../contexts/ModuleContext';
import { useTheme } from '../../contexts/ThemeContext';
import { fetchSiteData } from '../../services/siteDataService';
import { Download, MoreVertical, RefreshCw } from 'lucide-react';
import { Feature, isFeatureEnabled } from '../../config/features';

function rowNetVolume(row) {
  if (row.volume !== null && row.volume !== undefined) return Number(row.volume) || 0;
  return (Number(row.cut) || 0) - (Number(row.fill) || 0);
}

/** Aggregate net volume (m³) by zone name over the full in-memory dataset. */
function volumeByName(rows) {
  const map = new Map();
  rows.forEach((row) => {
    const name = String(row.name || '').trim() || '—';
    map.set(name, (map.get(name) || 0) + rowNetVolume(row));
  });
  return [...map.entries()]
    .map(([name, volume]) => ({ name, volume }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

export default function EarthworkPage() {
  const { isDarkMode } = useTheme();
  const { activeProject } = useModule();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    let isMounted = true;
    if (!activeProject?.numericId) {
      setData([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setCurrentPage(1);
    fetchSiteData(activeProject.numericId, 'Earth')
      .then((res) => {
        if (isMounted) {
          setData(res);
          setLoading(false);
        }
      })
      .catch((err) => {
        console.error('Error fetching earthwork site data:', err);
        if (isMounted) setLoading(false);
      });
    return () => {
      isMounted = false;
    };
  }, [activeProject?.numericId]);

  const totalArea = data.reduce((acc, row) => acc + (Number(row.area) || 0), 0);
  const totalCut = data.reduce((acc, row) => acc + (Number(row.cut) || 0), 0);
  const totalFill = data.reduce((acc, row) => acc + (Number(row.fill) || 0), 0);
  const totalNetVolume = data.reduce((acc, row) => acc + rowNetVolume(row), 0);

  const summaryByName = useMemo(() => volumeByName(data), [data]);

  const paginatedData = data.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);
  const rowMenu = isFeatureEnabled('EARTHWORK_ROW_MENU');

  const cardBg = isDarkMode ? '#111622' : '#FFFFFF';
  const cardBorder = isDarkMode ? '#1E2433' : '#E5E7EB';
  const muted = isDarkMode ? '#9CA3AF' : '#6B7280';
  const text = isDarkMode ? '#E5E7EB' : '#1F2937';
  const titleColor = isDarkMode ? '#F3F4F6' : '#111827';
  const rowBorder = isDarkMode ? '#1E2433' : '#F3F4F6';
  const headBg = isDarkMode ? '#161B26' : '#F9FAFB';

  return (
    <ConstructionLayout
      title="Earthwork"
      subtitle="Excavation and cut/fill volume from visual analysis · all surveys of this project"
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        <Feature name="EARTHWORK_EXPORT">
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
          <button
            type="button"
            disabled
            title="Export is not available yet"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 14px',
              borderRadius: '8px',
              backgroundColor: cardBg,
              border: `1px solid ${isDarkMode ? '#1E2433' : '#D1D5DB'}`,
              color: muted,
              fontSize: '13px',
              cursor: 'not-allowed',
              opacity: 0.55,
            }}
          >
            <Download size={14} /> Export
          </button>
        </div>
        </Feature>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: '20px' }}>
          <div
            style={{
              backgroundColor: cardBg,
              border: `1px solid ${cardBorder}`,
              borderRadius: '12px',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {loading ? (
              <div
                style={{
                  padding: '40px',
                  textAlign: 'center',
                  color: muted,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px',
                }}
              >
                <RefreshCw className="animate-spin" size={18} /> Loading earthwork records…
              </div>
            ) : data.length === 0 ? (
              <div style={{ padding: '40px', textAlign: 'center', color: muted }}>
                No Earthwork records found for this project.
              </div>
            ) : (
              <div
                style={{
                  overflowX: 'auto',
                  /* Keep body height stable when last page has fewer rows */
                  minHeight: `calc(${rowsPerPage} * 45px + 96px)`,
                }}
              >
                <table
                  style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: '12px',
                    textAlign: 'left',
                  }}
                >
                  <thead>
                    <tr
                      style={{
                        backgroundColor: headBg,
                        borderBottom: `1px solid ${cardBorder}`,
                        color: muted,
                      }}
                    >
                      <th style={{ padding: '12px 14px' }}>ID</th>
                      <th style={{ padding: '12px 14px' }}>Type</th>
                      <th style={{ padding: '12px 14px' }}>Name</th>
                      <th style={{ padding: '12px 14px' }}>Description</th>
                      <th style={{ padding: '12px 14px' }}>Area (m²)</th>
                      <th style={{ padding: '12px 14px' }}>Cut (m³)</th>
                      <th style={{ padding: '12px 14px' }}>Fill (m³)</th>
                      <th style={{ padding: '12px 14px' }}>Net Vol (m³)</th>
                      <th style={{ padding: '12px 14px' }}>Base Surface</th>
                      <th style={{ padding: '12px 14px' }}>Unit</th>
                      {rowMenu && <th style={{ padding: '12px 14px' }} />}
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedData.map((row) => {
                      const netVol = rowNetVolume(row);
                      const isNegative = netVol < 0;
                      return (
                        <tr
                          key={row.id}
                          style={{
                            borderBottom: `1px solid ${rowBorder}`,
                            color: text,
                          }}
                        >
                          <td
                            style={{
                              padding: '12px 14px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                            }}
                          >
                            <span
                              style={{
                                width: '6px',
                                height: '6px',
                                borderRadius: '50%',
                                backgroundColor:
                                  row.color === 'red' ? '#EF4444' : '#10B981',
                              }}
                            />
                            {row.id}
                          </td>
                          <td style={{ padding: '12px 14px' }}>{row.type}</td>
                          <td style={{ padding: '12px 14px', fontWeight: 600 }}>
                            {row.name}
                          </td>
                          <td
                            style={{
                              padding: '12px 14px',
                              maxWidth: '200px',
                              color: muted,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {row.description || '—'}
                          </td>
                          <td style={{ padding: '12px 14px' }}>
                            {row.area ? Number(row.area).toFixed(2) : '—'}
                          </td>
                          <td style={{ padding: '12px 14px' }}>
                            {row.cut !== null ? Number(row.cut).toFixed(2) : '0.00'}
                          </td>
                          <td style={{ padding: '12px 14px' }}>
                            {row.fill !== null ? Number(row.fill).toFixed(2) : '0.00'}
                          </td>
                          <td
                            style={{
                              padding: '12px 14px',
                              color: isNegative ? '#EF4444' : '#10B981',
                              fontWeight: 600,
                            }}
                          >
                            {netVol.toFixed(2)}
                          </td>
                          <td style={{ padding: '12px 14px' }}>
                            {row.base_surface || '—'}
                          </td>
                          <td style={{ padding: '12px 14px' }}>
                            {row.unit_system || 'metric'}
                          </td>
                          {rowMenu && (
                            <td style={{ padding: '12px 14px' }}>
                              <button
                                type="button"
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  color: '#9CA3AF',
                                  cursor: 'pointer',
                                }}
                              >
                                <MoreVertical size={14} />
                              </button>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                    <tr
                      style={{
                        backgroundColor: headBg,
                        fontWeight: 700,
                        color: titleColor,
                      }}
                    >
                      <td colSpan={4} style={{ padding: '14px' }}>
                        TOTAL ({data.length} zones)
                      </td>
                      <td style={{ padding: '14px' }}>{totalArea.toFixed(2)} m²</td>
                      <td style={{ padding: '14px' }}>{totalCut.toFixed(2)} m³</td>
                      <td style={{ padding: '14px' }}>{totalFill.toFixed(2)} m³</td>
                      <td
                        style={{
                          padding: '14px',
                          color: totalNetVolume < 0 ? '#EF4444' : '#10B981',
                        }}
                      >
                        {totalNetVolume.toFixed(2)} m³
                      </td>
                      <td colSpan={rowMenu ? 3 : 2} style={{ padding: '14px' }} />
                    </tr>
                  </tbody>
                </table>
              </div>
            )}

            {!loading && (
              <TablePagination
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                rowsPerPage={rowsPerPage}
                setRowsPerPage={setRowsPerPage}
                totalItems={data.length}
              />
            )}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div
              style={{
                backgroundColor: cardBg,
                border: `1px solid ${cardBorder}`,
                borderRadius: '12px',
                padding: '16px',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
              }}
            >
              <div style={{ fontSize: '14px', fontWeight: 700, color: titleColor }}>
                Summary
              </div>

              {loading ? (
                <div style={{ fontSize: '12px', color: muted }}>Loading…</div>
              ) : summaryByName.length === 0 ? (
                <div style={{ fontSize: '12px', color: muted }}>No volume data</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr auto',
                      gap: '8px',
                      padding: '6px 0',
                      borderBottom: `1px solid ${cardBorder}`,
                      fontSize: '11px',
                      fontWeight: 600,
                      color: muted,
                      textTransform: 'uppercase',
                      letterSpacing: '0.02em',
                    }}
                  >
                    <span>Item</span>
                    <span>Volume (m³)</span>
                  </div>
                  {summaryByName.map((row) => {
                    const negative = row.volume < 0;
                    return (
                      <div
                        key={row.name}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '1fr auto',
                          gap: '8px',
                          padding: '10px 0',
                          borderBottom: `1px solid ${rowBorder}`,
                          fontSize: '13px',
                        }}
                      >
                        <span style={{ color: text, fontWeight: 600 }}>{row.name}</span>
                        <span
                          style={{
                            fontWeight: 700,
                            fontVariantNumeric: 'tabular-nums',
                            color: negative ? '#EF4444' : '#10B981',
                          }}
                        >
                          {row.volume.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </ConstructionLayout>
  );
}
